import { IconButton } from "@nova/component-btn"
import { TriCheckbox } from "@nova/component-checkbox"
import * as Icons from "@nova/component-icons"
import { Table } from "@nova/ds-component-table"
import { MissionOriginRouterState } from "@nova/schemas"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { useMemo, useState } from "react"
import {
    selectChecklistFilter,
    selectDateFilter,
    selectDroneFilter,
    selectFlightsPage,
    selectFlightsSearch,
    selectPilotFilter,
    selectSelectedMissionIds,
    selectSortState,
    selectTagFilter,
} from "../redux/missions-table-selectors"
import {
    ALL_MISSIONS_TABLE_COLUMNS,
    clearSelectedMissionIds,
    setMissionSelected,
    setPage,
    setSortState,
    type MissionsTableColumn,
    type MissionsTableFilter,
    type SortField,
} from "../redux/missions-table-slice"
import { useMissionTableQuery, useMissionFlightPath } from "./PilotTrackingMissionQueries"
import { GroupContextMenu } from "./GroupContextMenu"
import { MissionRow } from "./MissionRow"
import { GroupRow } from "./GroupRow"
import { MissionDragChip } from "./MissionDragChip"
import { MissionsTableActionBar } from "./MissionsTableActionBar"
import { FlightPathPreviewPopup } from "./FlightPathPreviewPopup"
import { MissionsTableFilterBar } from "./MissionsTableFilterBar"

const SKELETON_WIDTHS = ["w-3/5", "w-4/5", "w-2/5", "w-3/4", "w-1/2"]

const SkeletonRow = ({
    columns,
    selectable,
    index,
}: {
    columns: MissionsTableColumn[]
    selectable: boolean
    index: number
}) => {
    const width = SKELETON_WIDTHS[index % SKELETON_WIDTHS.length]
    const bar = (w: string) => <div className={`h-3 ${w} rounded bg-gray-700 animate-pulse`} />
    return (
        <Table.Row>
            {selectable && <Table.Cell className="h-[68px]">{bar("w-4")}</Table.Cell>}
            <Table.Cell>{bar("w-3")}</Table.Cell>
            {columns.map((col) => (
                <Table.Cell key={col}>{bar(width)}</Table.Cell>
            ))}
            <Table.Cell />
        </Table.Row>
    )
}

const Filters = ({ filters }: { filters?: MissionsTableFilter[] }) => {
    const selectedMissionIds = useAppSelector(selectSelectedMissionIds)
    const numSelectedMissions = Object.keys(selectedMissionIds).length

    const page = useAppSelector(selectFlightsPage)
    const search = useAppSelector(selectFlightsSearch)
    const checklistFilter = useAppSelector(selectChecklistFilter)
    const sortState = useAppSelector(selectSortState)
    const dateFilter = useAppSelector(selectDateFilter)
    const pilotFilter = useAppSelector(selectPilotFilter)
    const droneFilter = useAppSelector(selectDroneFilter)
    const tagFilter = useAppSelector(selectTagFilter)

    const aircraftFilter = useMemo(() => {
        if (droneFilter?.id === "unassigned") return { unassignedAircraft: true } as const
        if (droneFilter && droneFilter.id) return { aircraft: [droneFilter.id] }
        return undefined
    }, [droneFilter])

    const { items: data, mutate } = useMissionTableQuery({
        page,
        limit: 20,
        filter: {
            search,
            missingChecklist: checklistFilter,
            dateFrom: dateFilter?.from,
            dateTo: dateFilter?.to,
            userId: pilotFilter?.id,
            tags: tagFilter.length > 0 ? tagFilter.map((t) => t.id) : undefined,
            ...aircraftFilter,
        },
        sort: sortState,
    })

    return (
        <div>
            {numSelectedMissions > 0 ? (
                <MissionsTableActionBar data={data} onMutate={mutate} />
            ) : (
                <MissionsTableFilterBar filters={filters} />
            )}
        </div>
    )
}

export const MissionsTable = ({
    columns = [...ALL_MISSIONS_TABLE_COLUMNS],
    selectable = true,
    editable = true,
    filters,
    origin,
}: {
    columns?: MissionsTableColumn[]
    selectable?: boolean
    editable?: boolean
    filters?: { aircraftId?: string; userId?: string }
    origin?: MissionOriginRouterState
}) => {
    const col = (c: MissionsTableColumn) => columns.includes(c)
    const dispatch = useAppDispatch()
    const [previewAnchor, setPreviewAnchor] = useState<{
        anchorRect: DOMRect
        missionId: string
    } | null>(null)
    const [hoveredMissionId, setHoveredMissionId] = useState<string | null>(null)
    const { points: hoveredPoints } = useMissionFlightPath(hoveredMissionId)

    const selectedMissionIds = useAppSelector(selectSelectedMissionIds)
    const numSelectedMissions = Object.keys(selectedMissionIds).length

    const page = useAppSelector(selectFlightsPage)
    const search = useAppSelector(selectFlightsSearch)
    const checklistFilter = useAppSelector(selectChecklistFilter)
    const sortState = useAppSelector(selectSortState)
    const dateFilter = useAppSelector(selectDateFilter)
    const pilotFilter = useAppSelector(selectPilotFilter)
    const droneFilter = useAppSelector(selectDroneFilter)
    const tagFilter = useAppSelector(selectTagFilter)

    const aircraftFilter = useMemo(() => {
        if (droneFilter?.id === "unassigned") return { unassignedAircraft: true } as const
        if (droneFilter && droneFilter.id) return { aircraft: [droneFilter.id] }
        return undefined
    }, [droneFilter])

    const {
        items: data,
        mutate,
        total,
        isLoading,
    } = useMissionTableQuery({
        page,
        limit: 20,
        filter: {
            search,
            missingChecklist: checklistFilter,
            dateFrom: dateFilter?.from,
            dateTo: dateFilter?.to,
            userId: filters?.userId ?? pilotFilter?.id,
            tags: tagFilter.length > 0 ? tagFilter.map((t) => t.id) : undefined,
            ...(filters?.aircraftId ? { aircraft: [filters.aircraftId] } : aircraftFilter),
        },
        sort: sortState,
    })

    const getSortClickHandler = (field: SortField) => {
        return () => {
            if (sortState.field !== field) {
                dispatch(setSortState({ dir: "ASC", field }))
            } else {
                dispatch(setSortState({ field, dir: sortState.dir === "ASC" ? "DESC" : "ASC" }))
            }
        }
    }

    const missionIds = useMemo(
        () =>
            data
                ?.flatMap((d) => (d.type === "mission" ? d : (d.missions ?? [])))
                .map((m) => m.id) ?? [],
        [data]
    )

    const isFiltered =
        !!search ||
        !!dateFilter ||
        !!pilotFilter ||
        !!droneFilter ||
        tagFilter.length > 0 ||
        checklistFilter

    return (
        <div className="flex flex-col w-full h-full overflow-hidden select-none">
            {!!data && (
                <Table className="table-fixed">
                    <Table.Head className="sticky top-0">
                        {selectable && (
                            <Table.Header className="w-10">
                                <TriCheckbox
                                    size="small"
                                    state={
                                        !numSelectedMissions
                                            ? "unchecked"
                                            : numSelectedMissions === missionIds.length
                                              ? "checked"
                                              : "indeterminate"
                                    }
                                    onClick={() => {
                                        if (numSelectedMissions === missionIds.length) {
                                            dispatch(clearSelectedMissionIds())
                                        } else {
                                            missionIds.forEach((id) =>
                                                dispatch(setMissionSelected({ id, selected: true }))
                                            )
                                        }
                                    }}
                                />
                            </Table.Header>
                        )}
                        <Table.Header className="w-8">
                            {/* Empty header for group chevron */}
                        </Table.Header>
                        {col("started_at") && (
                            <Table.Header
                                className="cursor-pointer select-none min-w-[246px]"
                                onClick={getSortClickHandler("started_at")}
                            >
                                <div className="flex items-center gap-1">
                                    <div>Date time</div>
                                    <Table.SortIndicator
                                        sortState={
                                            sortState.field === "started_at"
                                                ? sortState.dir
                                                : "none"
                                        }
                                    />
                                </div>
                            </Table.Header>
                        )}
                        {col("pilot") && (
                            <Table.Header className="min-w-[184px]">
                                <div>Pilot</div>
                            </Table.Header>
                        )}
                        {col("drone") && (
                            <Table.Header className="min-w-[134px]">
                                <div>Drone</div>
                            </Table.Header>
                        )}
                        {col("flightTime") && (
                            <Table.Header
                                className="cursor-pointer select-none min-w-[114px]"
                                onClick={getSortClickHandler("flightTime")}
                            >
                                <div className="flex items-center gap-1">
                                    <div>Flight time</div>
                                    <Table.SortIndicator
                                        sortState={
                                            sortState.field === "flightTime"
                                                ? sortState.dir
                                                : "none"
                                        }
                                    />
                                </div>
                            </Table.Header>
                        )}
                        {col("distanceFlown") && (
                            <Table.Header
                                className="cursor-pointer select-none min-w-[114px]"
                                onClick={getSortClickHandler("distanceFlown")}
                            >
                                <div className="flex items-center gap-1">
                                    <div>Distance flown</div>
                                    <Table.SortIndicator
                                        sortState={
                                            sortState.field === "distanceFlown"
                                                ? sortState.dir
                                                : "none"
                                        }
                                    />
                                </div>
                            </Table.Header>
                        )}
                        {col("maxAltitude") && (
                            <Table.Header
                                className="cursor-pointer select-none min-w-[114px]"
                                onClick={getSortClickHandler("maxAltitude")}
                            >
                                <div className="flex items-center gap-1">
                                    <div>Max altitude</div>
                                    <Table.SortIndicator
                                        sortState={
                                            sortState.field === "maxAltitude"
                                                ? sortState.dir
                                                : "none"
                                        }
                                    />
                                </div>
                            </Table.Header>
                        )}
                        {col("maxSpeed") && (
                            <Table.Header
                                className="cursor-pointer select-none min-w-[114px]"
                                onClick={getSortClickHandler("maxSpeed")}
                            >
                                <div className="flex items-center gap-1">
                                    <div>Max speed</div>
                                    <Table.SortIndicator
                                        sortState={
                                            sortState.field === "maxSpeed" ? sortState.dir : "none"
                                        }
                                    />
                                </div>
                            </Table.Header>
                        )}
                        {col("tags") && (
                            <Table.Header className="min-w-[164px]">
                                <div>Tags</div>
                            </Table.Header>
                        )}
                        {col("numChecklistSubmissions") && (
                            <Table.Header
                                className="cursor-pointer select-none min-w-[54px]"
                                onClick={getSortClickHandler("numChecklistSubmissions")}
                            >
                                <div className="flex items-center gap-1">
                                    <div>Checklist</div>
                                    <Table.SortIndicator
                                        sortState={
                                            sortState.field === "numChecklistSubmissions"
                                                ? sortState.dir
                                                : "none"
                                        }
                                    />
                                </div>
                            </Table.Header>
                        )}
                        <Table.Header className="sticky right-0 w-px max-w-px p-0" />
                    </Table.Head>
                    <Table.Body>
                        {isLoading && data.length === 0 ? (
                            SKELETON_WIDTHS.map((_, i) => (
                                <SkeletonRow
                                    key={i}
                                    columns={columns}
                                    selectable={selectable}
                                    index={i}
                                />
                            ))
                        ) : !isLoading && data.length === 0 ? (
                            <Table.Row>
                                <Table.Cell
                                    colSpan={columns.length + (selectable ? 3 : 2)}
                                    className="border-b-0"
                                >
                                    <div className="flex flex-col items-center justify-center py-16 gap-2">
                                        <h3 className="text-white">
                                            {isFiltered ? "No Results Found" : "No Flights Yet"}
                                        </h3>
                                        <p className="text-sm text-gray-400">
                                            {isFiltered
                                                ? "Try adjusting your search terms."
                                                : "Flights will appear here automatically once added."}
                                        </p>
                                    </div>
                                </Table.Cell>
                            </Table.Row>
                        ) : (
                            data.map((d) =>
                                d.type === "group" ? (
                                    <GroupRow
                                        key={d.id}
                                        group={d}
                                        selectedIds={selectedMissionIds}
                                        onMutate={mutate}
                                        columns={columns}
                                        selectable={selectable}
                                        editable={editable}
                                        origin={origin}
                                        onPreviewChange={setPreviewAnchor}
                                        onHoverChange={setHoveredMissionId}
                                    />
                                ) : (
                                    <MissionRow
                                        key={d.id}
                                        mission={d}
                                        selectedIds={selectedMissionIds}
                                        onMutate={mutate}
                                        columns={columns}
                                        selectable={selectable}
                                        editable={editable}
                                        origin={origin}
                                        onPreviewChange={setPreviewAnchor}
                                        onHoverChange={setHoveredMissionId}
                                    />
                                )
                            )
                        )}
                    </Table.Body>
                    <Table.Footer>
                        <div className="flex items-center gap-2">
                            <IconButton
                                className="flex items-center"
                                disabled={page === 1}
                                onClick={() => {
                                    dispatch(clearSelectedMissionIds())
                                    dispatch(setPage(Math.max(page - 1, 1)))
                                }}
                            >
                                <Icons.SmallIcon Icon={Icons.ChevronLeft} />
                                Prev
                            </IconButton>
                            <h5 className="text-gray-400">
                                Page {page} of {Math.max(1, Math.ceil(total / 20))}
                            </h5>
                            <IconButton
                                className="flex items-center"
                                onClick={() => {
                                    dispatch(clearSelectedMissionIds())
                                    dispatch(
                                        setPage(
                                            Math.min(page + 1, Math.max(1, Math.ceil(total / 20)))
                                        )
                                    )
                                }}
                                disabled={page === Math.max(1, Math.ceil(total / 20))}
                            >
                                Next
                                <Icons.SmallIcon Icon={Icons.ChevronRight} />
                            </IconButton>
                        </div>
                    </Table.Footer>
                </Table>
            )}
            <GroupContextMenu data={data ?? []} />
            <MissionDragChip />
            <FlightPathPreviewPopup previewAnchor={previewAnchor} points={hoveredPoints} />
        </div>
    )
}

MissionsTable.Filters = Filters
